import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../shared/tempo';
import type { WeaponDefinition } from './types';

const EVERY_BEAT = [0, 4, 8, 12];

export const WEAPONS: readonly WeaponDefinition[] = [
  {
    id: 'baton-de-feu',
    name: 'Bâton de feu',
    description: 'Il frappe en arc devant toi à chaque temps.',
    roles: ['hitFront'],
    rhythm: { everyBars: 1, steps: EVERY_BEAT },
    effect: { kind: 'sweep', damage: 12, radius: 90, arcDegrees: 120 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'baton-du-diable',
    name: 'Bâton du diable',
    description: 'Après chaque kick, trois étincelles qui transpercent.',
    roles: ['hitFar'],
    classAffinity: 'mage',
    rhythm: { everyBars: 1, steps: [1, 2, 3, 5, 6, 7, 9, 10, 11, 13, 14, 15] },
    effect: { kind: 'spark', damage: 3.8, speed: 18, pierce: 3, rangeTicks: 24 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'cerceaux',
    name: 'Cerceaux',
    description: 'Un cerceau tourne autour de toi, repousse et s’élargit.',
    roles: ['hitAround', 'push'],
    classAffinity: 'mage',
    rhythm: { everyBars: 1, steps: [0, 8] },
    effect: { kind: 'hoop', damage: 18, radius: 70, wideRadius: 120, knockback: 30 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'diabolo',
    name: 'Diabolo',
    description: 'Lancé sur le 1, il retombe sur les bad vibes et claque.',
    roles: ['hitFar'],
    classAffinity: 'mage',
    rhythm: { everyBars: 1, steps: [0] },
    effect: { kind: 'lob', damage: 45, radius: 80, range: 260, flightTicks: TICKS_PER_BAR / 2 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'frisbee',
    name: 'Frisbee',
    description: 'Il frappe à l’aller, revient soigner un allié.',
    roles: ['hitFar', 'heal'],
    rhythm: { everyBars: 1, steps: [4, 12] },
    effect: { kind: 'boomerang', damage: 21, heal: 9, range: 280, speed: 16 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'assiettes-chinoises',
    name: 'Assiettes chinoises',
    description: 'Ses zones freinent les bad vibes et soignent tes alliés.',
    roles: ['slow', 'heal'],
    rhythm: { everyBars: 1, steps: [8] },
    effect: {
      kind: 'plate',
      slowFactor: 0.6,
      healPerBar: 6,
      radius: 70,
      durationBars: 4,
      maxPlaced: 3,
    },
    maxLevel: 3,
    levelMul: 1.38,
    shade: 70,
  },
  {
    id: 'totem',
    name: 'Totem',
    description: 'Il attire les bad vibes, puis les repousse d’une onde.',
    roles: ['pull', 'push'],
    rhythm: { everyBars: 2, steps: [0] },
    effect: { kind: 'totem', damage: 75, knockback: 40, radius: 120, durationBars: 4 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'eventails-de-feu',
    name: 'Éventails de feu',
    description: 'Deux éventails tournent autour de toi sans jamais s’arrêter.',
    roles: ['hitAround'],
    rhythm: 'continuous',
    effect: { kind: 'orbit', damage: 9, count: 2, radius: 22, orbitRadius: 70, turnsPerBar: 2 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'monocycle',
    name: 'Monocycle',
    description: 'Tu roules vite ; ta traînée freine les bad vibes et soigne.',
    roles: ['slow', 'heal'],
    rhythm: 'continuous',
    effect: {
      kind: 'trail',
      speedMul: 1.2,
      slowFactor: 0.7,
      healPerBar: 4.5,
      lengthTicks: 2 * TICKS_PER_BEAT,
    },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'ruban-arc-en-ciel',
    name: 'Ruban arc-en-ciel',
    description: 'Les bad vibes qu’il traverse encaissent plus de coups.',
    roles: ['mark'],
    unlockedBySpeakers: 4,
    rhythm: { everyBars: 1, steps: [2, 6, 10, 14] },
    effect: { kind: 'ribbon', length: 220, markedTicks: TICKS_PER_BAR, markedDamageMul: 1.5 },
    maxLevel: 3,
    levelMul: 1.38,
  },
  {
    id: 'double-baton',
    name: 'Double bâton',
    description: 'Deux bâtons frappent tout autour de toi à chaque temps.',
    roles: ['hitAround'],
    evolvedFrom: 'baton-de-feu',
    rhythm: { everyBars: 1, steps: EVERY_BEAT },
    effect: { kind: 'sweep', damage: 16, radius: 110, arcDegrees: 360 },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'anneaux-solaires',
    name: 'Anneaux solaires',
    description: 'Trois cerceaux, chacun frappe comme ta spéciale.',
    roles: ['hitAround', 'push'],
    classAffinity: 'mage',
    evolvedFrom: 'cerceaux',
    rhythm: { everyBars: 1, steps: [0, 8] },
    effect: { kind: 'hoop', damage: 30, radius: 90, wideRadius: 170, knockback: 50 },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'pluie-de-diabolos',
    name: 'Pluie de diabolos',
    description: 'Sur le drop, une pluie de diabolos tombe sur tout l’écran.',
    roles: ['hitFar'],
    classAffinity: 'mage',
    evolvedFrom: 'diabolo',
    rhythm: { everyBars: 1, steps: EVERY_BEAT },
    effect: {
      kind: 'lob',
      damage: 60,
      radius: 110,
      range: 320,
      flightTicks: TICKS_PER_BAR / 2,
      dropRain: true,
    },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'frisbees-en-passes',
    name: 'Frisbees en passes',
    description: 'Il va plus loin et plus vite, puis revient soigner un allié.',
    roles: ['hitFar', 'heal'],
    evolvedFrom: 'frisbee',
    rhythm: { everyBars: 1, steps: [4, 12] },
    effect: { kind: 'boomerang', damage: 28, heal: 12, range: 340, speed: 18 },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'totem-sono',
    name: 'Totem sono',
    description: 'Le totem envoie une onde sur chaque kick.',
    roles: ['pull', 'push'],
    evolvedFrom: 'totem',
    rhythm: { everyBars: 1, steps: EVERY_BEAT },
    effect: { kind: 'totem', damage: 40, knockback: 50, radius: 150, durationBars: 6 },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'arc-en-ciel-du-drop',
    name: 'Arc-en-ciel du drop',
    description: 'Sur le drop, il traverse tout l’écran et marque tout.',
    roles: ['mark'],
    evolvedFrom: 'ruban-arc-en-ciel',
    rhythm: { everyBars: 1, steps: [2, 6, 10, 14] },
    effect: {
      kind: 'ribbon',
      length: 600,
      markedTicks: 2 * TICKS_PER_BAR,
      markedDamageMul: 1.5,
      dropCrossesArena: true,
    },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'monocycle-filant',
    name: 'Monocycle filant',
    description: 'Chaque dash laisse ta traînée au sol deux mesures.',
    roles: ['slow', 'heal'],
    evolvedFrom: 'monocycle',
    rhythm: 'continuous',
    effect: {
      kind: 'trail',
      speedMul: 1.35,
      slowFactor: 0.5,
      healPerBar: 8,
      lengthTicks: 4 * TICKS_PER_BEAT,
      dashTrailTicks: 2 * TICKS_PER_BAR,
    },
    maxLevel: 1,
    levelMul: 1,
  },
  {
    id: 'monocycle-reparateur',
    name: 'Monocycle réparateur',
    description: 'Tu files plus vite ; ta traînée freine plus et soigne mieux.',
    roles: ['slow', 'heal'],
    evolvedFrom: 'monocycle',
    rhythm: 'continuous',
    effect: {
      kind: 'trail',
      speedMul: 1.35,
      slowFactor: 0.5,
      healPerBar: 8,
      lengthTicks: 4 * TICKS_PER_BEAT,
    },
    maxLevel: 1,
    levelMul: 1,
  },
];
